// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import createUser from "@insite/client-framework/Store/Pages/UserList/Handlers/CreateUser";
import translate from "@insite/client-framework/Translate";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import { Checkbox, Radio, RadioGroup } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import AlertInfo from "@pacific/XDL/Icons/AlertInfo";
import { color, modalButtonsActionStyles, xScreen } from "@pacific/XDL/Utilities";
import { maxLengthAttr } from "@pacific/XDL/Utilities";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { PureComponent, ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface XngAccountModel extends AccountModel {
    xngAvailableRolesTranslated: any;
}

interface OwnProps {
    isOpen?: boolean;
    assignApproverHelpMessage?: string;
    roleInformation?: string;
    onClickCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onClose: (event?: React.SyntheticEvent<Element, Event>) => void;
}

const mapStateToProps = (state: CustomAppState) => ({
    currentAccountStateValue: getCurrentAccountState(state).value as XngAccountModel,
    currentAccountState: getCurrentAccountState(state),
    showUserName: !getSettingsCollection(state).accountSettings.useEmailAsUserName,
    // XNG-Change
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    createUser,
    loadAccount,
};

export interface CreateUserModalState {
    userName: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    approver: string;
    allowFinance: string;
    userNameErrorMessage?: ReactNode;
    emailErrorMessage?: ReactNode;
    firstNameErrorMessage?: ReactNode;
    lastNameErrorMessage?: ReactNode;
    showFormErrors: boolean;
    roleInformationModalIsOpen: boolean;
    approvers: OptionObject[];
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface CreateUserModalStyles {
    modal?: ModalPresentationProps;
    form?: InjectableCss;
    container?: GridContainerProps;
    separator?: GridItemProps;
    userNameVisibleGridItem?: GridItemProps;
    userNameHiddenGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameTextField?: TextFieldPresentationProps;
    settingsHeadingGridItem?: GridItemProps;
    settingsHeadingText?: TypographyPresentationProps;
    roleGridItem?: GridItemProps;
    roleSelect?: RadioGroupPresentationProps;
    roleSelectLabel?: InjectableCss;
    roleSelectHelpIcon?: IconPresentationProps;
    roleSelectHelpModal?: ModalPresentationProps;
    approverGridItem?: GridItemProps;
    approverSelect?: SelectPresentationProps;
    approverSelectTooltip?: TooltipPresentationProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // XNG-Change
    userRoleTooltip?: TooltipPresentationProps;
    roleSelectIconWrapper?: InjectableCss;
    approverIconWrapper?: InjectableCss;
}

// XNG-Changes: styling
export const createUserModalStyles: CreateUserModalStyles = {
    ...modalButtonsActionStyles,
    container: { gap: 15 },
    separator: {
        width: 12,
        css: css`
            &&& {
                padding: 0;
                margin: 12px 8px;
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    userNameVisibleGridItem: { width: [12, 12, 12, 6, 6] },
    userNameHiddenGridItem: {
        css: css`
            display: none;
        `,
    },
    emailGridItem: { width: [12, 12, 12, 6, 6] },
    firstNameGridItem: { width: [12, 12, 12, 6, 6] },
    lastNameGridItem: { width: [12, 12, 12, 6, 6] },
    settingsHeadingGridItem: { width: 12 },
    settingsHeadingText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    roleGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            position: relative;
        `,
    },
    roleSelect: {
        css: css`
            line-height: 18px;
            min-height: 18px;
            &&& {
                legend {
                    margin-bottom: 8px;
                    font-weight: normal;
                    font-size: 12px;
                }
            }
        `,
    },
    roleSelectLabel: {
        css: css`
            display: flex;
            align-items: center;
            line-height: 18px;
            min-height: 18px;
            &&& {
                font-weight: normal;
                font-size: 12px;
            }
        `,
    },
    roleSelectHelpIcon: {
        src: AlertInfo,
        size: 16,
        color: "primary",
        css: css`
            margin-left: 5px;
            cursor: pointer;
        `,
    },
    approverGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
            position: relative;
        `,
    },
    approverSelect: {
        css: css`
            width: 100%;
            margin-bottom: 15px;
        `,
        labelProps: {
            css: css`
                display: flex;
                line-height: 18px;
                min-height: 18px;
            `,
        },
    },
    approverSelectTooltip: {
        iconProps: {
            src: AlertInfo,
            size: 16,
            color: "primary",
        },
        cssOverrides: {
            tooltipClickable: css`
                margin: 0 5px 2px;
            `,
        },
    },
    userRoleTooltip: {
        iconProps: {
            src: HelpCircle,
            size: 14,
            color: "primary",
        },
        typographyProps: {
            css: css`
                font-weight: normal;
                display: flex;
            `,
        },
    },
    roleSelectIconWrapper: {
        css: css`
            position: absolute;
            left: 105px;
            top: 2px;
            ${xScreen("lte", "md")} {
                top: 8px;
            }
        `,
    },
    approverIconWrapper: {
        css: css`
            position: absolute;
            left: 103px;
            top: 8px;
            display: flex;
            align-items: center;
            .tooltip-container {
                display: flex;
                align-items: center;
            }
        `,
    },
    approverDynamicDropdown: {
        cssOverrides: {
            option: css`
                padding: 0 2px 1px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                color: ${getColor("text.main")};
                &:hover {
                    background: ${getColor("common.ui8")};
                    color: ${getColor("common.background")};
                }
            `,
            dropdownWrapper: css`
                width: 100%;
                margin-bottom: 15px;
                && > input {
                    &::placeholder {
                        color: ${getColor("text.main")};
                    }
                }
            `,
        },

        labelProps: {
            css: css`
                display: flex;
                line-height: 18px;
                min-height: 18px;
            `,
        },
    },
};

const styles = createUserModalStyles;

const StyledForm = getStyledWrapper("form");

class CreateUserModal extends React.Component<Props, CreateUserModalState> {
    state = {
        userName: "",
        email: "",
        firstName: "",
        lastName: "",
        role: "",
        approver: "",
        allowFinance: "false",
        userNameErrorMessage: undefined,
        emailErrorMessage: undefined,
        firstNameErrorMessage: undefined,
        lastNameErrorMessage: undefined,
        showFormErrors: false,
        roleInformationModalIsOpen: false,
        approvers: [],
    };

    componentWillUnmount(): void {
        if (this.isSafariDesktop()) {
            window.removeEventListener("keydown", this.handleKeydownTabInModal);
        }
    }

    handleKeydownTabInModal = (event: KeyboardEvent) => {
        event.stopImmediatePropagation();
    };

    isSafariDesktop = () => {
        const checkSafari = /(\bChrome\b|\bSafari\b)\//.exec(navigator.userAgent);
        return (
            checkSafari != null && checkSafari[1] !== "Chrome" && /\biPod\b|\biPad\b/g.exec(navigator.userAgent) == null
        );
    };

    usernameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ userName: event.target.value }, () => {
            this.state.showFormErrors && this.validateUserName();
        });
    };
    emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ email: event.target.value }, () => {
            this.state.showFormErrors && this.validateEmail();
        });
    };
    firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ firstName: event.target.value }, () => {
            this.state.showFormErrors && this.validateFirstName();
        });
    };
    lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ lastName: event.target.value }, () => {
            this.state.showFormErrors && this.validateLastName();
        });
    };
    roleChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ role: event.target.value });
    };
    approverChangeHandler = (value?: string) => {
        this.setState({ approver: value || "" });
    };
    allowFinanceChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        this.setState({ allowFinance: value.toString() });
    };
    validateUserName = () => {
        const userNameErrorMessage = !this.state.userName ? siteMessage("CreateNewAccountInfo_UserName_Required") : "";
        this.setState({ userNameErrorMessage });
        return !userNameErrorMessage;
    };
    validateEmail = () => {
        let emailErrorMessage: ReturnType<typeof siteMessage> = "";
        if (!this.state.email) {
            emailErrorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
        } else if (!validateEmail(this.state.email)) {
            emailErrorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
        }
        this.setState({ emailErrorMessage });
        return !emailErrorMessage;
    };
    validateFirstName = () => {
        const firstNameErrorMessage = !this.state.firstName ? siteMessage("User_Admin_Info_FirstName_Required") : "";
        this.setState({ firstNameErrorMessage });
        return !firstNameErrorMessage;
    };
    validateLastName = () => {
        const lastNameErrorMessage = !this.state.lastName ? siteMessage("User_Admin_Info_LastName_Required") : "";
        this.setState({ lastNameErrorMessage });
        return !lastNameErrorMessage;
    };
    validateForm = () => {
        const isEmailValid = this.validateEmail();
        const isFirstNameValid = this.validateFirstName();
        const isLastNameValid = this.validateLastName();
        let isFormValid = isEmailValid && isFirstNameValid && isLastNameValid;
        if (this.props.showUserName) {
            isFormValid = this.validateUserName() && isFormValid;
        }
        return isFormValid;
    };
    formSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!this.validateForm()) {
            this.setState({ showFormErrors: true });
            return false;
        }

        this.props.createUser({
            userName: this.state.userName,
            email: this.state.email,
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            role: this.state.role,
            approver: this.state.approver,
            properties: {
                allowFinance: this.state.allowFinance,
            },
            onError: (errorMessage: string) => {
                this.props.toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onSuccess: () => {
                this.props.toaster.addToast({
                    body: translate("User Created. An activation email has been sent."),
                    messageType: "success",
                });

                this.props.onClose();
                this.resetState();
            },
        } as any);
    };
    resetState = () => {
        this.setState({
            userName: "",
            email: "",
            firstName: "",
            lastName: "",
            role: "",
            approver: "",
            userNameErrorMessage: undefined,
            emailErrorMessage: undefined,
            firstNameErrorMessage: undefined,
            lastNameErrorMessage: undefined,
            showFormErrors: false,
        });
    };
    closeModalHandler = (event: React.SyntheticEvent<Element, Event>) => {
        this.props.onClose(event);
        this.resetState();
    };
    cancelFormHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.props.onClickCancel(event);
        this.resetState();
    };
    onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.debouncedSearchApprovers(event.target.value);
    };
    debouncedSearchApprovers = debounce((filterValue: string) => {
        this.setApproverOptions(filterValue);
    }, 300);
    componentDidUpdate() {
        if (this.state.approvers.length === 0) {
            this.setApproverOptions("");
        }

        // XNG-Change: disable keydown event on safari to fix tabbing issue PPSC-1118
        if (this.props.isOpen && this.isSafariDesktop()) {
            window.addEventListener("keydown", this.handleKeydownTabInModal, true);
        }
    }
    setApproverOptions = (filterValue: string) => {
        const currentAccountId = this.props.currentAccountState.id;
        if (!currentAccountId) {
            return;
        }
        let filteredApprovers: string[] | null = [];
        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;
        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }
        this.props.loadAccount({
            ...parameter,
            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }
                filteredApprovers = result?.apiResult?.availableApprovers;
                if (filteredApprovers) {
                    approverOptions = approverOptions.concat(
                        filteredApprovers.map(o => ({ optionText: o, optionValue: o })),
                    );
                }
                this.setState({ approvers: approverOptions, approver: "" });
            },
        });
    };

    render() {
        let availableRoles: string[] | null = [];
        let availableApprovers: string[] | null = [];
        if (this.props.currentAccountStateValue) {
            availableRoles = this.props.currentAccountStateValue.availableRoles;
            availableApprovers = this.props.currentAccountStateValue.availableApprovers;
        }
        const { userName, email, firstName, lastName, role, approver, showFormErrors } = this.state;
        const userNameGridItemStyles = this.props.showUserName
            ? styles.userNameVisibleGridItem
            : styles.userNameHiddenGridItem;

        // XNG-Change
        const roleSelectLabel = this.props.roleInformation ? (
            <>
                {/* XNG-Change */}
                <StyledWrapper {...styles.roleSelectIconWrapper}>
                    {this.props.isTabletScreen ? (
                        <Icon
                            {...styles.roleSelectHelpIcon}
                            onClick={() => this.setState({ roleInformationModalIsOpen: true })}
                        />
                    ) : (
                        <Typography onClick={() => this.setState({ roleInformationModalIsOpen: true })}>
                            <Tooltip {...styles.userRoleTooltip} text={translate("Click for more details")} />
                        </Typography>
                    )}
                </StyledWrapper>
                <Modal
                    {...styles.roleSelectHelpModal}
                    headline={translate("User Roles")}
                    isOpen={this.state.roleInformationModalIsOpen}
                    handleClose={() => this.setState({ roleInformationModalIsOpen: false })}
                >
                    {parse(this.props.roleInformation, parserOptions)}
                </Modal>
            </>
        ) : null;

        // XNG-Change
        const approverLabel = this.props.assignApproverHelpMessage ? (
            <StyledWrapper {...styles.approverIconWrapper}>
                <Tooltip {...styles.approverSelectTooltip} text={this.props.assignApproverHelpMessage} />
            </StyledWrapper>
        ) : null;

        return (
            <Modal
                {...styles.modal}
                handleClose={this.closeModalHandler}
                headline={translate("Create User")}
                isOpen={this.props.isOpen}
                data-test-selector="createUserModal"
            >
                <StyledForm {...styles.form} noValidate onSubmit={this.formSubmitHandler}>
                    {/* <XNG-Changes>: remove require */}
                    <GridContainer {...styles.container}>
                        {this.props.showUserName && (
                            <GridItem {...userNameGridItemStyles}>
                                <TextField
                                    {...styles.userNameTextField}
                                    error={showFormErrors && this.state.userNameErrorMessage}
                                    label={translate("Username")}
                                    onChange={this.usernameChangeHandler}
                                    value={userName}
                                    data-test-selector="createUserModal_userNameText"
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.emailGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                error={showFormErrors && this.state.emailErrorMessage}
                                label={translate("Email")}
                                onChange={this.emailChangeHandler}
                                value={email}
                                maxLength={maxLengthAttr.email}
                                data-test-selector="createUserModal_emailText"
                            />
                        </GridItem>
                        <GridItem {...styles.firstNameGridItem}>
                            <TextField
                                {...styles.firstNameTextField}
                                error={showFormErrors && this.state.firstNameErrorMessage}
                                label={translate("First Name")}
                                onChange={this.firstNameChangeHandler}
                                value={firstName}
                                data-test-selector="createUserModal_firstNameText"
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                error={showFormErrors && this.state.lastNameErrorMessage}
                                label={translate("Last Name")}
                                onChange={this.lastNameChangeHandler}
                                value={lastName}
                                data-test-selector="createUserModal_lastNameText"
                            />
                        </GridItem>
                        {/* <XNG-Changes>: add seperator */}
                        <GridItem {...styles.separator}></GridItem>
                        {/* </XNG-Changes> */}
                        <GridItem {...styles.settingsHeadingGridItem}>
                            <Typography {...styles.settingsHeadingText} as="h6">
                                {translate("Settings")}
                            </Typography>
                        </GridItem>
                        {availableRoles && (
                            <>
                                <GridItem {...styles.roleGridItem}>
                                    {/* <XNG-Changes>: switch to Radio */}
                                    <RadioGroup
                                        label={translate("Assign User Role")}
                                        value={role}
                                        onChangeHandler={this.roleChangeHandler}
                                        {...styles.roleSelect}
                                    >
                                        {availableRoles?.map(roleKey => (
                                            <Radio key={roleKey} value={roleKey}>
                                                {
                                                    this.props.currentAccountStateValue?.xngAvailableRolesTranslated?.[
                                                        roleKey
                                                    ]
                                                }
                                            </Radio>
                                        ))}
                                    </RadioGroup>
                                    {/* </XNG-Changes> */}
                                    {roleSelectLabel}
                                </GridItem>
                            </>
                        )}
                        <GridItem {...styles.approverGridItem}>
                            {availableApprovers && (
                                <>
                                    <DynamicDropdown
                                        label={translate("Assign Approver")}
                                        {...styles.approverDynamicDropdown}
                                        isLoading={this.props.currentAccountState.isLoading}
                                        onSelectionChange={this.approverChangeHandler}
                                        onInputChange={this.onInputChanged}
                                        selected={approver}
                                        options={this.state.approvers}
                                        data-test-selector="createUserModal_approverSelect"
                                        placeholder={translate("Select an Approver")}
                                    />

                                    {/* </XNG-Changes> */}
                                    {approverLabel}
                                </>
                            )}
                            <Checkbox
                                checked={this.state.allowFinance === "true"}
                                onChange={this.allowFinanceChangeHandler}
                            >
                                {translate("Allow Finance")}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.buttonsGridItem}>
                            <Button
                                {...styles.cancelButton}
                                data-test-selector="createUserModal_cancelButton"
                                onClick={this.cancelFormHandler}
                                type="button"
                                aria-label="Cancel"
                            >
                                {translate("Cancel")}
                            </Button>
                            <Button
                                {...styles.submitButton}
                                data-test-selector="createUserModal_createUserButton"
                                type="submit"
                                aria-label="Create User"
                            >
                                {translate("Create User")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                    {/* </XNG-Changes> */}
                </StyledForm>
            </Modal>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(CreateUserModal));
